import {
  afterNextRender,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  PLATFORM_ID,
  resource,
  ResourceRef,
  signal
} from '@angular/core';

import {SessionResources} from '../../../../resources/session.resources';
import {ActivatedRoute} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {capitalize} from '../../../../common/strings';
import {DateTime} from 'luxon';
import {LeadResources} from '../../../../resources/lead.resources';
import {FIREBASE_AUTH} from '../../../config/module.di.tokens';
import {ageThreads} from '../../../../common/sessions';
import {SubscriptionContextV2Service} from '../../../../services/subscription-v2-context.service';
import {SocialTrackService} from '../../../../services/social-track.service';
import {nullToEmptyArray, shuffle} from '../../../../common/objects';
import Swiper from 'swiper';
import {Autoplay} from 'swiper/modules';
import {SessionService} from '../../../../services/session.service';
import {ShowContentComponent} from '../show-content/show-content.component';
import {SeoService} from '../../../../services/seo.service';
import {SessionTopic} from '../../../../model/session.model';
import {environment} from '../../../../environments/environment';
import {SessionDTO, SessionSubscribedDTO} from '@minutespourtoi/api-models/dist/models';
import {SessionSubscribedDTOWithUid} from '../../../../data/dto/session.dto';

@Component({
  selector: 'app-show-session',
  imports: [
    ShowContentComponent,


  ],
  templateUrl: './show-session.component.html',
})
export class ShowSessionComponent {

  protected readonly ageThreads = ageThreads;

  title = inject(Title)
  route = inject(ActivatedRoute)
  sessionResources = inject(SessionResources)
  socialTrackService = inject(SocialTrackService);
  leadResources : LeadResources
  auth = inject(FIREBASE_AUTH)
  subscriptionContextV2Service = inject(SubscriptionContextV2Service)
  sessionService = inject(SessionService)
  swiper: Swiper

  platformId = inject(PLATFORM_ID);
  isBrowser = isPlatformBrowser(this.platformId);

  sex = input<string>()
  age = input<string>()
  sessionId = input<string>()
  seo = inject(SeoService)

  subscribedPeople = signal<Array<SessionSubscribedDTOWithUid>>([])
  stayingPlaces = computed<number | null>(() => {
    const session = this.session.value();
    let stayingPlaces = null;
    if (session) {
      const filtered = Object.values(session.stayingPlacesByCat ?? {}).filter((n): n is number => n != null);
      stayingPlaces = filtered.length > 0 ? filtered.reduce((left, right) => left + right, 0) : null;
    }

    return stayingPlaces
  });

  // FOR SSG AND BROWSER
  session = resource({
    request: () => ({ id: this.route.snapshot.params['sessionId'] }),
    loader: async ({ request }) => {
      let session: SessionDTO = null
      if (request?.id) {
        session = await this.sessionService.session(request.id);
      }
      return session
    }
  }) as ResourceRef<SessionDTO>;

  // FOR SSG AND BROWSER
  topic = resource({
    request: () => this.session.value(),
    loader: async ({ request: session }) => this.sessionService.topicOfSession(session)
  }) as ResourceRef<SessionTopic>;

  constructor() {

    const injector = inject(Injector);

    effect(async () => {
      const session = this.session.value();
      const topic = this.topic.value();
      if(session && topic) {
        const people: Array<SessionSubscribedDTOWithUid> = (session.subscribedPeople ?? [])
          .map((p, index) => ({ ...p, __uid: index }));

        this.subscribedPeople.set(people);

        if (this.isBrowser) {
          afterNextRender(() => {
            this.subscribedPeople.set(this.shufflePeople(session.subscribedPeople));
          }, { injector });

          setTimeout(() => this.loadSlider(), 600);
        }
        await this.loadLeadContext();
        this.loadSeo(session, topic);
      }
    });
  }

  private async loadLeadContext() {
    if(this.sex() && this.age()) {
      this.subscriptionContextV2Service.sex.set(this.sex() === 'f' ? 'F' : 'M');
      this.subscriptionContextV2Service.age.set(this.age());
    }
    if(!this.subscriptionContextV2Service.defaultOffer()) {
      await this.subscriptionContextV2Service.detectDefaultOffer()
    }
  }

  private loadSeo(session: SessionDTO, topic: SessionTopic) {
    if(session && topic) {
      const dateStr = capitalize(DateTime.fromMillis(session.date).setLocale('fr').toFormat('cccc DD'));
      const seoData = {
        title: `Speed dating du ${dateStr} à ${session.cityName}`,
        description: topic.subtitle,
        image: `${environment.mediaDnsUrl}/topics/${topic.name}.jpg`,
        url: `show/${session.id}`
      };
      this.seo.updateMetadata(seoData)
    }
  }

  private shufflePeople(subscribedPeople: Array<SessionSubscribedDTO>) {
    const people = nullToEmptyArray(subscribedPeople);
    if (people.length <= 5) {
      return people;
    }
    return shuffle(people)
      .map(subscribed => ({
        ...subscribed,
        aboutMe: shuffle(nullToEmptyArray(subscribed.aboutMe)).slice(0, 2),
        hobbies: shuffle(nullToEmptyArray(subscribed.hobbies)).slice(0, 2),
      }))
      .sort((left, right) =>
        (right.aboutMe.length + right.hobbies.length) - (left.aboutMe.length + left.hobbies.length)
      );
  }

  private loadSlider() {
    this.swiper = new Swiper(".people-carousel", {
      slidesPerView: 1,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      speed: 350,
      modules: [Autoplay],
    });
  }

}
